// Dashboard (`/`, WEB_PLAN.md §5.3). Admins and managers: the period's KPIs over the people they can see (computed
// from the replica like iOS Reports), who is in the field now, this week's top five, what needs attention, and the
// team's latest activity. Reps get the same page limited to themselves, titled "My numbers", with KPIs from
// `rpc('team_metrics')` because their replica is filtered.
//
// Everything is a pure function of the shared data views (dashboardModel.ts); this file only wires hooks to it and
// memoizes per data change. A one-minute ticker keeps "now" (presence, "so far" ranges, relative times) current.

import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useSession } from '@/auth/AuthProvider';
import {
  useActivities,
  useDerivedStats,
  useMe,
  useMetricsIndex,
  usePipelines,
  useRecords,
  useRepPositions,
  useSettings,
  useUsers,
  useVisibleRecords,
  useVisibleUsers,
  useVisitResults,
} from '@/data/hooks';
import { startOfDay } from '@/domain/calendar';
import { teamMetrics } from '@/domain/metrics';
import { dayRange, goalPeriodRange, goalPeriodStart } from '@/domain/periods';
import { recordDisplayName } from '@/domain/records';
import { lastActivityByUser } from '@/domain/team';
import { byId } from '@/domain/util';
import { RequireFirstSync, ScreenError, ScreenLoading, useDocumentTitle } from '@/features/common';
import { useCan } from '@/session/permissions';
import { PageContainer, PageHeader, SegmentedControl, useTicker } from '@/ui';
import type { GoalPeriod } from '@/contracts/enums';
import { ActivityFeedCard } from './ActivityFeedCard';
import {
  DASHBOARD_PERIODS,
  DASHBOARD_PERIOD_TITLES,
  PERIOD_QUERY_KEY,
  feedModel,
  kpiTiles,
  myMetricsRow,
  myPipeline,
  needsAttention,
  periodFromParam,
  periodParam,
  periodRangeLabel,
  repKpiState,
  repNumbers,
  storeLinks,
  teamNowRows,
  teamNumbers,
  topUsers,
  ZERO_NUMBERS,
} from './dashboardModel';
import { KpiGrid } from './KpiGrid';
import { LeaderboardCard } from './LeaderboardCard';
import { NeedsAttentionCard } from './NeedsAttentionCard';
import { TeamNowCard } from './TeamNowCard';
import { useTeamMetricsQuery } from './useMyMetrics';

const PERIOD_OPTIONS = DASHBOARD_PERIODS.map((p) => ({ value: p, label: DASHBOARD_PERIOD_TITLES[p] }));

export default function DashboardPage() {
  const can = useCan();
  const self = can.dashboardScope === 'self';
  const title = self ? 'My numbers' : 'Dashboard';
  useDocumentTitle(title);

  const session = useSession();
  const settings = useSettings();
  const [params, setParams] = useSearchParams();
  const period = periodFromParam(params.get(PERIOD_QUERY_KEY));
  const tick = useTicker(60_000);
  // `tick` is the clock: a new "now" once a minute (it is not read inside on purpose).
  const now = useMemo(() => new Date(), [tick]);
  const range = useMemo(() => goalPeriodRange(period, now), [period, now]);

  const setPeriod = (next: GoalPeriod) => {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        const value = periodParam(next);
        if (value) out.set(PERIOD_QUERY_KEY, value);
        else out.delete(PERIOD_QUERY_KEY);
        return out;
      },
      { replace: true },
    );
  };

  const company = settings.companyName.trim() || session.companyName;
  return (
    <PageContainer>
      <PageHeader
        title={title}
        subtitle={`${company} · ${DASHBOARD_PERIOD_TITLES[period]}, ${periodRangeLabel(period, range)}`}
        actions={
          <SegmentedControl label="Period" value={period} onChange={setPeriod} options={PERIOD_OPTIONS} size="sm" />
        }
      />
      <RequireFirstSync what={title} fallback={<ScreenLoading layout="cards" count={6} label={`Loading ${title.toLowerCase()}`} />}>
        <DashboardBody period={period} self={self} now={now} />
      </RequireFirstSync>
    </PageContainer>
  );
}

function DashboardBody({ period, self, now }: { period: GoalPeriod; self: boolean; now: Date }) {
  const session = useSession();
  const viewerId = session.profileId;
  const settings = useSettings();
  const activities = useActivities();
  const records = useRecords();
  const visibleRecords = useVisibleRecords();
  // Feed rows name and link only stores the viewer can open (the store page looks among these, archived included).
  const openableRecords = useVisibleRecords({ includeArchived: true });
  const users = useUsers();
  const visibleUsers = useVisibleUsers();
  const visitResults = useVisitResults();
  const pipelines = usePipelines();
  const index = useMetricsIndex();
  const stats = useDerivedStats();
  const positions = useRepPositions();
  const me = useMe();

  const range = useMemo(() => goalPeriodRange(period, now), [period, now]);
  const dayStart = startOfDay(now).getTime();
  const resultsById = useMemo(() => byId(visitResults.values), [visitResults]);
  const usersById = useMemo(() => byId(users.values), [users]);

  // Reps: their numbers come from the server (the hook is idle for admins and managers).
  const metrics = useTeamMetricsQuery(period, range.from, self);
  const repState = repKpiState(metrics);

  // Keyed on the period start, not "now": the minute ticker must not re-run ReportEngine over every activity.
  const fromMs = range.from.getTime();
  const numbers = useMemo(() => {
    if (self) {
      if (!metrics.data) return ZERO_NUMBERS;
      return repNumbers(myMetricsRow(metrics.data, viewerId), myPipeline(records.values, pipelines.values, viewerId));
    }
    return teamNumbers(
      {
        visibleUsers: visibleUsers.values,
        activities: activities.values,
        records: records.values,
        visitResults: visitResults.values,
        pipelines: pipelines.values,
        // Distance is not on the dashboard; skip summing day logs.
        dayLogs: [],
      },
      { from: new Date(fromMs) },
    );
  }, [self, metrics.data, viewerId, records, pipelines, visibleUsers, activities, visitResults, fromMs]);

  const links = useMemo(
    () => storeLinks({ range, now, pipelines: pipelines.values, visitResults: visitResults.values }),
    [range, now, pipelines, visitResults],
  );
  const tiles = useMemo(() => kpiTiles(numbers, links, { currencyCode: settings.currencyCode }), [numbers, links, settings.currencyCode]);

  // Today's per-person counts in one pass (each row equals repSummary's visits / contacts for that person).
  const today = useMemo(() => {
    const day = dayRange(new Date(dayStart));
    return teamMetrics(index, day.from, day.to);
  }, [index, dayStart]);
  const lastActivity = useMemo(() => lastActivityByUser(activities.values), [activities]);
  const teamNow = useMemo(
    () =>
      teamNowRows({
        users: self ? (me && !me.undecodable ? [me.value] : []) : visibleUsers.values,
        positions,
        today,
        lastActivity,
        visitResults: resultsById,
        viewerId,
        now,
        everyone: self,
      }),
    [self, me, visibleUsers, positions, today, lastActivity, resultsById, viewerId, now],
  );

  // "Team this week": the week so far, in one pass for everybody (equal to leaderboard(.visits, .weekly)).
  const leaders = useMemo(() => {
    if (self) return [];
    const week = teamMetrics(index, goalPeriodStart('weekly', now), now);
    return topUsers(week, visibleUsers.values, { metric: 'visits', limit: 5, viewerId });
  }, [self, index, now, visibleUsers, viewerId]);

  const attention = useMemo(
    () =>
      needsAttention({
        activities: activities.values,
        records: visibleRecords.values,
        scope: self ? 'self' : 'team',
        visibleUserIds: visibleUsers.values.map((u) => u.id),
        viewerId,
        stats,
        pipelines: pipelines.values,
        visitResults: resultsById,
        users: usersById,
        now,
      }),
    [activities, visibleRecords, self, visibleUsers, viewerId, stats, pipelines, resultsById, usersById, now],
  );

  const feed = useMemo(
    () =>
      feedModel({
        activities: activities.values,
        visibleUsers: visibleUsers.values,
        users: usersById,
        visitResults: resultsById,
        recordName: (id) => {
          const item = openableRecords.get(id);
          return item && !item.undecodable ? recordDisplayName(item.value) : undefined;
        },
        // Only the "today" count depends on the clock; the day's start is enough for it.
        now: new Date(dayStart),
        userIds: self ? [viewerId] : undefined,
        limit: 50,
      }),
    [activities, visibleUsers, usersById, resultsById, openableRecords, dayStart, self, viewerId],
  );

  const kpiLabel = `${self ? 'My numbers' : 'Key metrics'}, ${DASHBOARD_PERIOD_TITLES[period]}`;

  return (
    <div className="flex flex-col gap-6">
      {self && repState === 'error' ? (
        <ScreenError
          compact
          title="Couldn't load your numbers"
          error={metrics.error}
          onRetry={() => void metrics.refetch()}
          className="rounded-card border border-line bg-surface"
        />
      ) : (
        <div className="flex flex-col gap-2">
          <KpiGrid tiles={tiles} loading={self && repState === 'loading'} label={kpiLabel} />
          {self && repState === 'stale' && (
            <p role="status" className="flex flex-wrap items-center gap-x-2 px-1 text-xs text-fg-muted">
              <span>Couldn't refresh your numbers. These are from the last update.</span>
              <button
                type="button"
                onClick={() => void metrics.refetch()}
                disabled={metrics.isFetching}
                className="font-semibold text-primary-text hover:underline disabled:opacity-60"
              >
                {metrics.isFetching ? 'Retrying…' : 'Retry'}
              </button>
            </p>
          )}
        </div>
      )}

      {/* DOM order is the reading and Tab order at every width: Team now, Needs attention (+ Leaderboard), then the
          long feed. At xl the side column spans both rows; the `1fr` second row takes any extra height it adds, so
          the feed starts right under Team now. (Reordering with `order` would leave keyboard focus jumping.) */}
      <div className="flex flex-col gap-6 xl:grid xl:grid-cols-3 xl:grid-rows-[auto_1fr] xl:items-start">
        <TeamNowCard
          rows={teamNow}
          now={now}
          linkToTeam={!self}
          self={self}
          title={self ? 'Today' : 'Team now'}
          className="xl:col-span-2 xl:col-start-1 xl:row-start-1"
        />
        <div className="flex min-w-0 flex-col gap-6 xl:col-start-3 xl:row-span-2 xl:row-start-1">
          <NeedsAttentionCard data={attention} links={links} now={now} />
          {!self && <LeaderboardCard entries={leaders} />}
        </div>
        <ActivityFeedCard
          feed={feed}
          now={now}
          self={self}
          className="xl:col-span-2 xl:col-start-1 xl:row-start-2"
        />
      </div>
    </div>
  );
}
